import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import Button from '../Button'
import Icon from '../Icon'
import Logo from '../Logo'
import { useEscape, useScrollLock, useScrolled } from '../../lib/hooks'
import { company, nav } from '../../data/site'

/**
 * Sticky header.
 *
 * At the top of the page it is transparent and draws in white over the dark
 * hero band that every route opens with; past 24px it becomes a translucent
 * white bar. The mobile drawer closes on route change and on Escape, and locks
 * body scroll while open.
 */
export default function Header() {
  const scrolled = useScrolled(24)
  const [menuOpen, setMenuOpen] = useState(false)
  const { pathname } = useLocation()

  // Close the drawer whenever the route changes.
  useEffect(() => {
    setMenuOpen(false)
  }, [pathname])

  useEscape(() => setMenuOpen(false), menuOpen)
  useScrollLock(menuOpen)

  // A drawer left open while the viewport grows would hide behind the desktop
  // nav and keep the scroll lock on.
  useEffect(() => {
    if (!menuOpen) return undefined
    const query = window.matchMedia('(min-width: 64.0625rem)')
    const onChange = (event) => {
      if (event.matches) setMenuOpen(false)
    }
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [menuOpen])

  const state = scrolled || menuOpen ? 'scrolled' : 'top'

  return (
    <>
      <header className={`site-header site-header--${state}`}>
        <div className="container site-header__inner">
          <Logo />

          <nav className="nav nav--desktop" aria-label="Hauptnavigation">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `nav__link${isActive ? ' nav__link--active' : ''}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="header__actions">
            <Button to="/kontakt" size="sm" icon="arrowRight">
              Termin vereinbaren
            </Button>

            <button
              type="button"
              className="nav-toggle"
              aria-expanded={menuOpen}
              aria-controls="mobile-drawer"
              aria-label={menuOpen ? 'Menü schließen' : 'Menü öffnen'}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="nav-toggle__bars" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>
          </div>
        </div>
      </header>

      {menuOpen ? (
        <div className="drawer" id="mobile-drawer">
          <nav className="drawer__nav" aria-label="Hauptnavigation (mobil)">
            {[...nav, { label: 'Kontakt', to: '/kontakt' }].map(
              (item, index) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `drawer__link${isActive ? ' drawer__link--active' : ''}`
                  }
                >
                  {item.label}
                  <span>{String(index + 1).padStart(2, '0')}</span>
                </NavLink>
              ),
            )}
          </nav>

          <div className="drawer__footer">
            <div className="drawer__contact">
              <a href={company.phoneHref}>
                <Icon name="phone" />
                <span>{company.phone}</span>
              </a>
              <a href={company.emailHref}>
                <Icon name="mail" />
                <span>{company.email}</span>
              </a>
            </div>
            <Button to="/kontakt" icon="arrowRight" block>
              Termin vereinbaren
            </Button>
          </div>
        </div>
      ) : null}
    </>
  )
}
